import { type IAcademicUnit } from './AcademicUnit'
import { type ICreatedBy, type DataObjectList, type IOwnedBy, type IModifiedBy, type DataObject } from './DataObject'
import { type SlotEmailSettings } from './SlotEmailSettings'
import { type ITerm } from './Term'

export enum SlotStatus {
  Draft = 'Draft',
  Published = 'Published',
  Live = 'Live',
  Closed = 'Closed'
}

export enum SurveyProjectType {
  VERIFIED = 'verified',
  UNVERIFIED = 'unverified'
}
export interface ISlotNodeWarningDivision {
  abbreviation: string
  title: string
}

export interface ISlotNodeWarning {
  key: 'warning' | 'error'
  value: string
  divisions: ISlotNodeWarningDivision[]
}

export interface ISlotNodeDetails {
  courseCount: number
  slotStatus: SlotStatus
  studentCount: number
  warnings?: ISlotNodeWarning[]
}
export interface ISurveyTemplate {
  title: string
  templateId: string
  templateRevision: number
}

export interface ISlotDateSettings {
  enabled: boolean
  noOfDays: number
  sectionFieldType: string
  slotId: string
  slotNodeSurveyDateSettingId: string
  surveyDateType: string
};

export interface InstructorAddedQuestions {
  isCustomQuestionAllowed?: boolean
  questionCount?: number
  isInstructorOptOutAllowed?: boolean
  defaultTemplateId?: string
  defaultTemplateRevision?: number
  defaultTemplateTitle?: string | null
}

/**
 * Section-added questions (CR-295): one shared set per course section, asked once, editable by
 * any instructor on that section. Distinct from `InstructorAddedQuestions`, which repeats per
 * instructor — that is the whole point of the type, and the two are never merged.
 *
 * Shaped identically to its sibling so the settings block can mirror it field for field.
 */
export interface SectionAddedQuestions {
  isCustomQuestionAllowed?: boolean
  questionCount?: number
  defaultTemplateId?: string
  defaultTemplateRevision?: number
  defaultTemplateTitle?: string | null
}

export interface ILinkedSection {
  abbreviation: string
  sectionId: string
  title: string
}
export interface AcademicLevelAddedQuestions {
  academicUnitCategories: IAcademicUnit[]
  isAcademicLevelOptOutAllowed?: boolean
  isCustomQuestionAllowed: boolean
  questionCount: number
}

export interface AcademicUnit {
  abbrevation: string
  categoryName: string
  depth: number
  divisionId: string
  title: string
}

export interface ISlot extends ICreatedBy, IOwnedBy, IModifiedBy {
  isArchived: boolean
  releaseTimestamp: Date | string
  slotStartTimestamp: Date
  slotEndTimestamp: Date
  slotCloseTimestamp: string
  slotId: string
  slotLiveTimestamp: string | null
  slotNodeDetails: ISlotNodeDetails
  /**
   * Sandbox mode (CR-341). Absent → treated as false in UI.
   *
   * On the **term**, not the Survey Project: a project is not rehearsed, one of its terms is
   * (backend `80a0e6006`, 2026-09-19). While true, only users holding a role with
   * `roleType === 'GLOBAL'` can see this term, no email is sent for it to anyone, and its
   * lifecycle freezes at Published. Enforcement is server-side; this flag only drives
   * presentation.
   */
  isSandbox?: boolean
  slotTypeId: string | null
  surveyTemplate: ISurveyTemplate | null
  term: ITerm
  title: string
  slotDateSettings: ISlotDateSettings[]
  isCourseAttributeFilterActive: boolean
  isReportingHierarchyFilterActive: boolean
  isSyncAllSectionsForTermActive: boolean
  minimumEnrollment: number | null
  isInstructorAddedQuestionsAllowed?: boolean | null
  isAcademicLevelAddedQuestionsAllowed?: boolean | null
  // Backend column lives on Slot, not on SlotAllowableQuestionSettings — dumped as a
  // sibling of instructorAddedQuestions, never nested inside it (slot_schema.py:108,111).
  isDefaultInstructorAddedQuestionsEnabled?: boolean
  instructorAddedQuestions?: InstructorAddedQuestions | null
  academicLevelAddedQuestions?: AcademicLevelAddedQuestions | null
  /**
   * CR-295 — whether the Course Section question type is on for this project.
   *
   * Read it with `??`, never `||`: an explicit `false` is authoritative. Turning the type off PATCHes
   * only this flag and leaves `sectionAddedQuestions` behind (the row holds the limit), so treating a
   * surviving row as "still on" makes a completed turn-off read as on again on reopen.
   *
   * Absent on a project enabled under the earlier one-call flow, before this column existed — the
   * one case where falling back to `sectionAddedQuestions != null` is right.
   */
  isSectionAddedQuestionsAllowed?: boolean | null
  /**
   * CR-295 — the type's settings: its question limit and whether instructors may author their own
   * prompts. An attached `defaultTemplateId` means a default template is applied, which is optional.
   *
   * Written by `PATCH /slots/<slot_id>` (Draft only) once the flag above is on; `default_template_*`
   * is rejected there and belongs to
   * `POST /slots/<slot_id>/section_added_questions/apply_default/`.
   *
   * Optional because a slot that never enabled the type carries nothing here.
   */
  sectionAddedQuestions?: SectionAddedQuestions | null
  linkedSection?: ILinkedSection
  academicUnits: AcademicUnit[]
  isRollingResultsEnabled: boolean
  isSlotAnonymous: boolean
  slotEmailSettings: SlotEmailSettings[]
  lastActiveSlotId: string | null
  modifiedOn: string
  modifiedByUsername: string
  isAcademicUnitOptOutEnabled: boolean
  isInstructorOptOutEnabled?: boolean
  instructorOptOutDeadline?: string
  academicUnitOptOutCategoryIds?: string[]
  academicUnitOptOutDeadline?: string
  isInstructorOptInOverrideEnabled?: boolean
  /** Denormalized from distribution series; read-only on slot GET. Absent → false. */
  isInstructorReleaseOnGradePostingEnabled?: boolean
}

export type IDistributionPlanRow = ISlot

export type IDistributionPlanList = DataObject<ISlot>

export type IDistributionPlanRowDataType = IDistributionPlanList[]

export type IDistributionPlan = DataObjectList<ISlot>
